import { Box, Typography } from '@mui/material';
import type { GemmaInlineOptionsGroup } from '../../utils/gemmaInlineOptions';

/**
 * Render klikalnych pigułek wygenerowanych przez Gemmę (marker `[[unitresco:options]]`).
 *
 * Każda grupa = osobny rząd pigułek (pod bąbelkiem, flex-wrap).
 * Klik w pigułkę → `onPick(send)` — zwykle skutkuje natychmiastową wysyłką `send`
 * jako user message do agenta.
 */
export default function GemmaInlineOptions({
  groups,
  onPick,
  disabled = false,
  testIdPrefix = 'gemma-inline-opt',
}: {
  groups: GemmaInlineOptionsGroup[];
  onPick: (send: string) => void;
  disabled?: boolean;
  testIdPrefix?: string;
}) {
  if (!groups || groups.length === 0) return null;
  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {groups.map((group, gi) => (
        <Box
          key={gi}
          sx={{
            pt: gi === 0 ? 1 : 0,
            borderTop: gi === 0 ? '1px solid rgba(2,2,66,0.12)' : 'none',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
          }}
        >
          {group.options.map((opt, oi) => (
            <Box
              key={oi}
              component="button"
              type="button"
              data-testid={`${testIdPrefix}-${gi}-${oi}`}
              onClick={() => {
                if (!disabled) onPick(opt.send);
              }}
              disabled={disabled}
              title={opt.send === opt.label ? undefined : opt.send}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                maxWidth: '100%',
                border: '1px solid rgba(63,63,220,0.35)',
                borderRadius: '999px',
                backgroundColor: 'rgba(63,63,220,0.06)',
                color: '#020242',
                cursor: disabled ? 'default' : 'pointer',
                py: 0.4,
                px: 1.1,
                font: 'inherit',
                fontSize: 12.5,
                lineHeight: 1.25,
                textAlign: 'left',
                transition: 'background-color 120ms ease',
                '&:hover': disabled ? {} : { backgroundColor: 'rgba(63,63,220,0.14)' },
                '&:disabled': { opacity: 0.6 },
              }}
            >
              <Typography
                component="span"
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: 12.5,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {opt.label}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}
